import {
    type ApiContainerPlanActionSource,
    type ApiContainerPlanSource,
    type ApiPlanContainerRequest,
    type ApiPlannedContainerAction,
    type EnumContainerActionRequest,
    EnumPlannedActionSource,
} from '@Model';

/** What a planned container action is based on. */
export type ContainerPlanSource =
    /** Containers standing at a site, which stand for one customer at one site. */
    | { standingTimeIds: string[] }
    /**
     * A container action an order, an order snapshot or a requisition plans, and how many alike rows of the order it
     * stands for; one unless named.
     */
    | { plannedAction: ApiPlannedContainerAction; count?: number }
    /** Several container actions of orders or requisitions, which stand for one customer at one site. */
    | { plannedActions: ApiPlannedContainerAction[] }
    /** A container action of the order that is open, copied as it is, as many times as named; once unless. */
    | { draftContainerActionId: string; count?: number };

const noSource: ApiContainerPlanSource = {
    standing_time_ids: undefined,
    planned_actions: undefined,
    order_id: undefined,
    order_snapshot_id: undefined,
    requisition_id: undefined,
    container_action_id: undefined,
    draft_container_action_id: undefined,
};

const noParent: ApiContainerPlanActionSource = {
    order_id: undefined,
    order_snapshot_id: undefined,
    requisition_id: undefined,
    container_action_id: undefined,
};

function plannedActionParent(line: ApiPlannedContainerAction): ApiContainerPlanActionSource {
    switch (line.source) {
        case EnumPlannedActionSource.Order:
            return { ...noParent, order_id: line.source_id, container_action_id: line.id };
        case EnumPlannedActionSource.OrderSnapshot:
            return { ...noParent, order_snapshot_id: line.source_id, container_action_id: line.id };
        case EnumPlannedActionSource.Requisition:
            return { ...noParent, requisition_id: line.source_id, container_action_id: line.id };
    }
}

/** The source as the server takes it. */
// @ai-generated 2026-10-09T21:30:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function toApiSource(source: ContainerPlanSource): ApiContainerPlanSource {
    if ('standingTimeIds' in source) {
        return { ...noSource, standing_time_ids: source.standingTimeIds };
    }

    if ('plannedAction' in source) {
        return { ...noSource, ...plannedActionParent(source.plannedAction) };
    }

    if ('plannedActions' in source) {
        return { ...noSource, planned_actions: source.plannedActions.map(plannedActionParent) };
    }

    return { ...noSource, draft_container_action_id: source.draftContainerActionId };
}

/** The request that has the server plan an action, from a source if there is one. */
// @ai-generated 2026-10-09T21:30:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function toPlanRequest(
    action: EnumContainerActionRequest | undefined,
    source?: ContainerPlanSource,
    replacementContainerTypeId?: string,
): ApiPlanContainerRequest {
    return {
        action,
        source: source === undefined ? undefined : toApiSource(source),
        replacement_container_type_id: replacementContainerTypeId,
        count: source !== undefined && 'count' in source ? source.count ?? 1 : 1,
    };
}
